import React, { useState, useEffect } from 'react';
import { TextField, Button } from '@material-ui/core';
import { Link, useHistory, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import { authService } from '../../services/authService';
import "./Login.css";
import learningCube from '../../assets/images/learning-strategy/learning_cube.svg';

function ResetPasswordForm() {
    const history = useHistory();
    const { token } = useParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Validate password strength
    const validatePassword = (password) => {
        const errors = {};
        if (password.length < 6) {
            errors.length = 'Password must be at least 6 characters long';
        }
        return errors;
    };

    // Validate form
    const validateForm = () => {
        const errors = {};
        
        if (!password) {
            errors.password = 'Password is required';
        } else {
            const passwordErrors = validatePassword(password);
            if (Object.keys(passwordErrors).length > 0) {
                errors.password = Object.values(passwordErrors)[0];
            }
        }

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        try {
            setError('');
            setValidationErrors({});
            setLoading(true);
            
            await authService.resetPassword(token, password);
            
            setSuccess(true);
            // Redirect after showing success message
            setTimeout(() => {
                history.push('/login');
            }, 2000);
            
        } catch (error) {
            setError(error.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Check if token is present
    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token. Please request a new password reset.');
        }
    }, [token]);

    if (success) {
        return (
            <div className="login-page-container">
                <header className="landing-header">
                    <img 
                        src="https://ia.intelladapt.ai/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Flogo.bb151063.png&w=256&q=75" 
                        alt="Learning Strategy Logo" 
                        className="landing-logo-img" 
                    />
                    <nav className="landing-nav">
                        <Link to="/">About</Link>
                        <Link to="/contact">Contact</Link>
                        <Link to="/login" className="landing-nav-login-btn">Login</Link>
                    </nav>
                </header>
                <div className="login">
                    <div className="login-content">
                        <div className="learning-cube-image">
                            <img 
                                src={learningCube} 
                                alt="Learning Cube" 
                                className="cube-img"
                            />
                        </div>
                        <div className="quiz__main">
                            <div className="success-container" style={{ textAlign: 'center', padding: '40px 20px' }}>
                                <h2 style={{ color: '#4CAF50', marginBottom: '20px' }}>✅ Password Reset Successful!</h2>
                                <p style={{ marginBottom: '30px' }}>Your password has been successfully updated.</p>
                                <p>Redirecting to login page...</p>
                                <Link to="/login" className="login-button" style={{ 
                                    display: 'inline-block', 
                                    padding: '12px 24px',
                                    textDecoration: 'none',
                                    marginTop: '20px'
                                }}>
                                    Go to Login
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page-container">
            <header className="landing-header">
                <img 
                    src="https://ia.intelladapt.ai/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Flogo.bb151063.png&w=256&q=75" 
                    alt="Learning Strategy Logo" 
                    className="landing-logo-img" 
                />
                <nav className="landing-nav">
                    <Link to="/">About</Link>
                    <Link to="/contact">Contact</Link>
                    <Link to="/login" className="landing-nav-login-btn">Login</Link>
                </nav>
            </header>
            <div className="login">
                <div className="login-content">
                    <div className="learning-cube-image">
                        <img 
                            src={learningCube} 
                            alt="Learning Cube" 
                            className="cube-img"
                        />
                    </div>
                    <div className="quiz__main">
                        <h2 className="forgot-password-title">Set New Password</h2>
                        <p className="forgot-password-subtitle">
                            Enter your new password below. Make sure it's secure and easy to remember.
                        </p>
                        
                        <div className="loginForm_main">
                            {error && <Alert severity="error" className="error_login">{error}</Alert>}
                            
                            <form onSubmit={handleSubmit} className="lForm__main">
                                <TextField
                                    required
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="quizForm__textfield"
                                    fullWidth
                                    label="New Password"
                                    variant="filled"
                                    error={!!validationErrors.password}
                                    helperText={validationErrors.password || 'Minimum 6 characters'}
                                />
                                <TextField
                                    required
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="quizForm__textfield"
                                    fullWidth
                                    label="Confirm New Password"
                                    variant="filled"
                                    error={!!validationErrors.confirmPassword}
                                    helperText={validationErrors.confirmPassword}
                                />
                                
                                <Button 
                                    type="submit"
                                    className="login-button"
                                    disabled={loading || !token}
                                    style={{ marginTop: '24px' }}
                                >
                                    {loading ? 'Resetting Password...' : 'Reset Password'}
                                </Button>
                            </form>
                            
                            <div className="back-to-login" style={{ marginTop: '20px', textAlign: 'center' }}>
                                <Link to="/login" className="back-link">
                                    ← Back to Login
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPasswordForm; 